import type { AxialCoord } from '@/types/hex.types';
import type { HexGrid } from '@/hex/HexGrid';
import { hexDistance, coordToKey, getNeighbors, coordsEqual } from '@/hex/HexCoord';

/**
 * Priority queue node for A* pathfinding
 */
interface PathNode {
  coord: AxialCoord;
  gCost: number; // Cost from start
  hCost: number; // Heuristic cost to end
  fCost: number; // Total cost (g + h)
  parent: PathNode | null;
}

/**
 * A* pathfinding on hex grid
 */
export class PathfindingSystem {
  private hexGrid: HexGrid;

  constructor(hexGrid: HexGrid) {
    this.hexGrid = hexGrid;
  }

  /**
   * Find shortest path between two coordinates
   * @param start Starting coordinate
   * @param end Target coordinate
   * @param blockedCoords Set of coordinates to avoid (occupied tiles)
   * @returns Array of coordinates representing the path (excluding start), or null if no path exists
   */
  findPath(
    start: AxialCoord,
    end: AxialCoord,
    blockedCoords: Set<string> = new Set()
  ): AxialCoord[] | null {
    // If start equals end, no path needed
    if (coordsEqual(start, end)) {
      return [];
    }

    // Check if end is valid
    if (!this.hexGrid.isValidCoord(end)) {
      return null;
    }

    const openSet: PathNode[] = [];
    const closedSet = new Set<string>();

    // Start node
    const startNode: PathNode = {
      coord: start,
      gCost: 0,
      hCost: hexDistance(start, end),
      fCost: hexDistance(start, end),
      parent: null,
    };
    openSet.push(startNode);

    while (openSet.length > 0) {
      // Get node with lowest fCost
      openSet.sort((a, b) => a.fCost - b.fCost || a.hCost - b.hCost);
      const current = openSet.shift()!;
      const currentKey = coordToKey(current.coord);

      // Check if we reached the goal
      if (coordsEqual(current.coord, end)) {
        return this.reconstructPath(current);
      }

      closedSet.add(currentKey);

      // Check all neighbors
      const neighbors = getNeighbors(current.coord);
      for (const neighborCoord of neighbors) {
        const neighborKey = coordToKey(neighborCoord);

        // Skip if already evaluated
        if (closedSet.has(neighborKey)) continue;

        // Skip if not valid coordinate
        if (!this.hexGrid.isValidCoord(neighborCoord)) continue;

        // Skip if blocked (but allow end coord even if blocked - for targeting)
        if (blockedCoords.has(neighborKey) && !coordsEqual(neighborCoord, end)) {
          continue;
        }

        const gCost = current.gCost + 1; // All hex moves cost 1
        const hCost = hexDistance(neighborCoord, end);
        const fCost = gCost + hCost;

        // Check if this path is better
        const existingNode = openSet.find((n) => coordToKey(n.coord) === neighborKey);
        if (existingNode) {
          if (gCost < existingNode.gCost) {
            existingNode.gCost = gCost;
            existingNode.fCost = fCost;
            existingNode.parent = current;
          }
        } else {
          openSet.push({
            coord: neighborCoord,
            gCost,
            hCost,
            fCost,
            parent: current,
          });
        }
      }
    }

    // No path found
    return null;
  }

  /**
   * Find path to get within attack range of target
   * @returns Path to a position within range of target, or null if unreachable
   */
  findPathToAttackRange(
    start: AxialCoord,
    target: AxialCoord,
    attackRange: number,
    blockedCoords: Set<string> = new Set()
  ): AxialCoord[] | null {
    // Check if already in range
    if (hexDistance(start, target) <= attackRange) {
      return [];
    }

    // Find all tiles within attack range of target that are unblocked
    const tilesInRange = this.getTilesInRange(target, attackRange, blockedCoords);

    if (tilesInRange.length === 0) {
      // Target is completely surrounded, try to get as close as possible
      return this.findPath(start, target, blockedCoords);
    }

    // Find path to closest tile in range
    let bestPath: AxialCoord[] | null = null;
    let bestDistance = Infinity;

    for (const tile of tilesInRange) {
      const path = this.findPath(start, tile, blockedCoords);
      if (path !== null && path.length < bestDistance) {
        bestPath = path;
        bestDistance = path.length;
      }
    }

    return bestPath;
  }

  /**
   * Get all valid tiles within range of a coordinate
   */
  private getTilesInRange(
    center: AxialCoord,
    range: number,
    blockedCoords: Set<string>
  ): AxialCoord[] {
    const result: AxialCoord[] = [];

    for (let q = -range; q <= range; q++) {
      for (let r = Math.max(-range, -q - range); r <= Math.min(range, -q + range); r++) {
        const coord: AxialCoord = { q: center.q + q, r: center.r + r };
        const key = coordToKey(coord);

        if (
          this.hexGrid.isValidCoord(coord) &&
          !blockedCoords.has(key) &&
          !coordsEqual(coord, center)
        ) {
          result.push(coord);
        }
      }
    }

    return result;
  }

  /**
   * Reconstruct path from end node to start
   */
  private reconstructPath(endNode: PathNode): AxialCoord[] {
    const path: AxialCoord[] = [];
    let current: PathNode | null = endNode;

    while (current !== null && current.parent !== null) {
      path.unshift(current.coord);
      current = current.parent;
    }

    return path;
  }

  /**
   * Check if there's a direct line of sight between two coordinates
   * (no blocking units between them)
   */
  hasLineOfSight(
    from: AxialCoord,
    to: AxialCoord,
    blockedCoords: Set<string>
  ): boolean {
    const distance = hexDistance(from, to);
    if (distance <= 1) return true;

    // Linear interpolation through hex grid
    for (let i = 1; i < distance; i++) {
      const t = i / distance;
      const x = from.q + (to.q - from.q) * t;
      const y = from.r + (to.r - from.r) * t;

      // Round to nearest hex
      const rx = Math.round(x);
      const ry = Math.round(y);

      if (blockedCoords.has(coordToKey({ q: rx, r: ry }))) {
        return false;
      }
    }

    return true;
  }

  /**
   * Get the next step along a path
   */
  getNextStep(path: AxialCoord[]): AxialCoord | null {
    return path.length > 0 ? path[0] : null;
  }
}
